import React from "react";
import { useDrag } from "react-use-gesture";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { useSpring, animated, interpolate } from "react-spring";
import useStyles from "./DragDropControl.styles";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import { ControlProps } from "./BaseControl";
import { useMentionValues } from "../../hooks/useMentions";
import useRecall from "../../hooks/useRecall";
import { ChoiceModel } from "../../models/ChoiceModel";
import { translate } from "../../models/utilities/translationUtilities";
import { useLanguage } from "../../hooks/useLanguage";
import { useValidation, useDiscreteAnswerValidator } from "../../hooks/useValidation";
import { usePrevious } from "../../hooks/usePrevious";
import { isIE } from "../../../src/utils";

const BUCKET_DIVIDER_SIZE = 16;
const MIN_CARD_HEIGHT = 47;
const CARD_SPACING = 15;
const BOUNDS_MARGIN = 90;

const ORIGIN = 0;
const DESTINATION = 1;

const insert = (l, i, x) => [...l.slice(0, x), i, ...l.slice(x)];

interface CardProps {
    id?: string;
    text: string;
    imageUrl: string;
    width: number;
    height: number;
    top: number;
    left: number;
    bounds: { top: number; bottom: number; left: number; right: number };
    onDrag(xy: number[]);
    onDrop(xy: number[]);
    setCardRef(id: string, ref: any): void;
    isDragAllowed: boolean;
}

const makeCardSpring = ({
    gestureState: {
        down,
        movement: [x, y],
    },
    left,
    top,
    immediate,
}: any) => ({
    x: down ? x : left,
    y: down ? y : top,
    z: down ? 10 : 0,
    immediate: (name) => (immediate || down) && ["x", "y"].includes(name),
    config: { mass: 1, tension: 170, friction: 25 },
});

function DragDropCard({
    id,
    text,
    imageUrl,
    width,
    height,
    top,
    left,
    onDrag,
    onDrop,
    bounds,
    setCardRef,
    isDragAllowed,
}: CardProps) {
    const recalledImage = useRecall(imageUrl);
    const recalledText = useRecall(text);
    const prevBounds = usePrevious(bounds);
    const theme = useTheme();
    const classes = useStyles({ theme });
    const drag = React.useRef<any>({ down: false, movement: [0, 0] });

    const [spring, set] = useSpring(() =>
        makeCardSpring({ gestureState: drag.current, left, top }),
    );

    const boundsChanged = prevBounds !== bounds;
    const isLegacyBrowser = isIE();

    React.useLayoutEffect(() => {
        set(makeCardSpring({ gestureState: drag.current, left, top, immediate: boundsChanged }));
    }, [left, top, set, boundsChanged]);

    const bind = useDrag(
        (state) => {
            drag.current = state;
            set(makeCardSpring({ gestureState: state, left, top }));
            const { movement, down } = state;
            if (down && onDrag) onDrag(movement);
            if (!down && onDrop) onDrop(movement);
        },
        {
            initial: () => [spring.x.getValue(), spring.y.getValue()],
            filterTaps: true,
            bounds: {
                ...bounds,
                right: bounds.right - width,
                bottom: bounds.bottom - height,
            },
        },
    );

    return (
        <animated.div
            id={id}
            ref={(ref) => setCardRef(id, ref)}
            style={{
                left: CARD_SPACING,
                top: CARD_SPACING * 2,
                width: width - CARD_SPACING * 2,
                minHeight: height,
                transform: interpolate(
                    [spring.x, spring.y, spring.z],
                    (x, y, z) => `translate(${x}px, ${y}px) scale(${z * 0.01 + 1})`,
                ),
                boxShadow: spring.z.interpolate((z) => `0 ${z * 0.3}px ${z + 3}px rgba(0,0,0,0.2)`),
                zIndex: spring.z.interpolate((z) => Math.max(1, 1 + Math.round(z))),
                backgroundImage: recalledImage && `url('${recalledImage}')`,
                cursor: isLegacyBrowser && "pointer",
            }}
            {...bind()}
            className={classnames(classes.card, isDragAllowed && classes.cardNoDrag)}
            dangerouslySetInnerHTML={recalledImage ? undefined : { __html: recalledText }}
            aria-label={recalledText}
        />
    );
}

interface BucketProps {
    origin?: boolean;
    destination?: boolean;
    width: number;
    height: number;
    left: number;
    top: number;
    isCursorNoDrag?: boolean;
}

export function DragDropBucket({
    origin,
    destination,
    width,
    height,
    left,
    top,
    isCursorNoDrag,
}: BucketProps) {
    const theme = useTheme();
    const classes = useStyles({ theme });

    return (
        <div
            className={classnames(
                classes.bucket,
                origin && classes.bucketOrigin,
                isCursorNoDrag && classes.bucketOriginNoDrag,
                destination && classes.bucketDestination,
            )}
            style={{ width, height, left, top }}
        />
    );
}

export function DragDropControl(props: ControlProps) {
    const { question, sequencedVariables, showSoftPrompt, validateSignal, max } = props;
    const variable = sequencedVariables[0];
    const [mentions, setMentions] = useMentionValues(variable.name);
    const [dragging, setDragging] = React.useState<ChoiceModel>();
    const [dropBucket, setDropBucket] = React.useState(0);
    const [dropIndex, setDropIndex] = React.useState(0);
    const [language] = useLanguage();
    const theme = useTheme();
    const [imageRatioByCard, setImageRatioByCard] = React.useState<{ [name: string]: number }>({});
    const classes = useStyles({ theme });
    const {
        ref: containerRef,
        dimensions: { innerWidth },
        calcDimensions,
    } = useElementDimensions<HTMLDivElement>();
    const [cardsRefs, setCardsRefs] = React.useState<{ [key: string]: any }>({});
    React.useLayoutEffect(calcDimensions, [cardsRefs]);

    const bucketWidth = innerWidth / 2 - BUCKET_DIVIDER_SIZE;

    const getCardHeight = (choice: ChoiceModel) =>
        Math.max(
            bucketWidth / imageRatioByCard[choice.name] || 0,
            MIN_CARD_HEIGHT,
            getCardRefHeight(choice),
        );

    const getCardRefHeight = ({ elementId }) => {
        const ref = cardsRefs[elementId];
        return ref ? ref.clientHeight : 0;
    };

    let [originChoices, destinationChoices] = [
        variable.choiceList
            .filter(({ value }) => !mentions.includes(value))
            .filter((choice) => choice !== dragging),
        mentions
            .map((value) => variable.choiceList.find((c) => c.value === value))
            .filter(Boolean)
            .filter((choice) => choice !== dragging),
    ];

    const onChoiceDrag = (choice: ChoiceModel) => ([x, y]: number[]) => {
        setDragging(choice);
        const cx = x + bucketWidth / 2;
        const bucket = cx < innerWidth / 2 ? ORIGIN : DESTINATION;
        const choices = bucket === ORIGIN ? originChoices : destinationChoices;
        setDropBucket(bucket);
        setDropIndex(getDropIndex(choices, y));
    };

    if (dragging && dropBucket === ORIGIN) {
        originChoices = insert(originChoices, dragging, dropIndex);
    }

    if (dragging && dropBucket === DESTINATION) {
        destinationChoices = insert(destinationChoices, dragging, dropIndex);
    }

    const originBucketHeight =
        variable.choiceList
            .map((choice) => getCardHeight(choice) + CARD_SPACING)
            .reduce((a, b) => a + b) +
        CARD_SPACING * 3;

    let dynamicArrayOfChoices: ChoiceModel[] = [];

    if (destinationChoices.length < Number(max) || max === undefined || Number(max) === 0) {
        dynamicArrayOfChoices = variable.choiceList;
    } else {
        dynamicArrayOfChoices = destinationChoices;
    }

    const destinationBucketHeight =
        dynamicArrayOfChoices
            .map((choice, index) => {
                return Number(max) > index ? getCardHeight(choice) + CARD_SPACING : 0;
            })
            .reduce((a, b) => a + b) +
        CARD_SPACING * 3;

    const isChoiceSelected = ({ value }) => mentions && mentions.includes(value);

    const getCardTop = (list, choice) =>
        list
            .slice(0, list.indexOf(choice))
            .map((c) => getCardHeight(c) + CARD_SPACING)
            .reduce((sum, x) => sum + x, 0);

    const getDropIndex = (list, y) => {
        const fl = list.filter((c) => c !== dragging);
        return fl.filter((c) => y > getCardTop(fl, c) + getCardHeight(c) / 2).length;
    };

    const borderWidth = 1;
    const destinationLeft = bucketWidth + BUCKET_DIVIDER_SIZE * 2 - borderWidth * 2;
    const bounds = React.useMemo(
        () => ({
            top: -BOUNDS_MARGIN,
            bottom: originBucketHeight + BOUNDS_MARGIN,
            left: -BOUNDS_MARGIN,
            right: innerWidth + BOUNDS_MARGIN,
        }),
        [innerWidth, originBucketHeight],
    );

    useValidation(validateSignal, useDiscreteAnswerValidator(question, variable, showSoftPrompt));

    React.useEffect(
        () =>
            variable.choiceList.forEach((card) => {
                const imageUrl = translate(card, "imageurl", language);
                if (imageUrl) {
                    const { name } = card;
                    const img = new Image();
                    img.addEventListener("load", () => {
                        const ratio = img.naturalWidth / img.naturalHeight;
                        setImageRatioByCard((state) => ({ ...state, [name]: ratio }));
                    });
                    img.src = imageUrl;
                }
            }),
        [variable.choiceList, language],
    );

    const onChoiceDrop = (choice: ChoiceModel) => () => {
        const { value } = choice;
        if (dropBucket === DESTINATION) {
            setMentions((b) => b.removeValue(value).insert(dropIndex, value));
        } else {
            setMentions((b) => b.removeValue(value));
        }
        setDragging(undefined);
    };

    const setCardRef = (id: string, ref: any) => {
        if (!cardsRefs[id] && ref) setCardsRefs((refs) => ({ ...refs, [id]: ref }));
    };

    const maxAnswers = Number.parseInt(max, 10);

    return (
        <div
            className={classes.container}
            ref={containerRef}
            style={{ height: originBucketHeight }}
        >
            <DragDropBucket
                origin
                width={bucketWidth}
                height={originBucketHeight}
                left={0}
                top={0}
                isCursorNoDrag={destinationChoices?.length >= maxAnswers}
            />
            <DragDropBucket
                destination
                width={bucketWidth}
                height={max === undefined ? originBucketHeight : destinationBucketHeight}
                left={destinationLeft}
                top={0}
            />

            {variable.choiceList.map((choice) => (
                <DragDropCard
                    isDragAllowed={
                        originChoices.includes(choice) && destinationChoices?.length >= maxAnswers
                    }
                    id={choice.elementId}
                    key={choice.name}
                    text={translate(choice, "text", language)}
                    imageUrl={translate(choice, "imageurl", language)}
                    width={bucketWidth}
                    left={isChoiceSelected(choice) ? destinationLeft : 0}
                    height={getCardHeight(choice)}
                    top={getCardTop(
                        isChoiceSelected(choice) ? destinationChoices : originChoices,
                        choice,
                    )}
                    bounds={bounds}
                    onDrag={onChoiceDrag(choice)}
                    onDrop={onChoiceDrop(choice)}
                    setCardRef={setCardRef}
                />
            ))}
        </div>
    );
}
